/**
 * Manager Status Hook
 *
 * Determines whether the currently authenticated user is a line manager by
 * querying the Dynamics 365 `EssWorkerManagerRelations` entity.
 *
 * A user is considered a manager when at least one relation record lists their
 * personnel number as the `ManagerPersonnelNumber`. The `PersonnelNumber` of
 * each such record is a person that user manages.
 *
 * Returns:
 *   - isManager: boolean               — true when the user manages ≥1 person
 *   - managedPersonnelNumbers: string[]— personnel numbers the user manages
 *   - isLoading: boolean               — profile or relations query in flight
 *   - error: Error | null              — relations query error, if any
 */
import { useQuery } from "react-query";
import { organizationService } from "../../api/services";
import useEmployeeProfile from "../useEmployeeProfile";

export default function useManagerStatus() {
  const { employeeNo, isLoading: profileLoading } = useEmployeeProfile();

  const {
    data,
    isLoading: relationsLoading,
    error,
  } = useQuery(
    ["managerStatus", employeeNo],
    async () => {
      console.log("🔍 Checking manager status for:", employeeNo);

      const response =
        await organizationService.getWorkerManagerRelations(employeeNo);
      const relations = response.data?.value || [];

      // Records where the current user is the manager → the people they manage.
      const managedPersonnelNumbers = relations
        .filter((relation) => relation.ManagerPersonnelNumber === employeeNo)
        .map((relation) => relation.PersonnelNumber)
        .filter(Boolean);

      const isManager = managedPersonnelNumbers.length > 0;

      console.log(
        `✅ Manager status: ${isManager ? "Manager" : "Not a manager"} — manages ${managedPersonnelNumbers.length} person(s)`,
      );

      return { isManager, managedPersonnelNumbers };
    },
    {
      enabled: !!employeeNo, // Only run once we know who the user is
      retry: 2,
      staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    },
  );

  return {
    isManager: data?.isManager ?? false,
    managedPersonnelNumbers: data?.managedPersonnelNumbers ?? [],
    isLoading: profileLoading || (!!employeeNo && relationsLoading),
    error: error || null,
  };
}
